import { AfterViewInit, ChangeDetectionStrategy, ChangeDetectorRef, Component, NgZone, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute, Router } from '@angular/router';
import { EMPTY, Observable, of, Subject, timer } from 'rxjs';
import { catchError, filter, map, switchMap, takeUntil, tap } from 'rxjs/operators';
import {
  AnalystProgressStep,
  AnalystTurnStatus,
  AnalystTurnStepSummary,
  ChatMessageResponse,
  ChatResponse,
  PatchChatRequest,
  ReportJobStatus,
} from 'src/app/models/chat.model';
import { AnalystStopReason, Citation } from 'src/app/models/analyst-output.model';
import { AiReportJobStatus } from 'src/app/enums/aiReportJobStatus.enum';
import { AnalystTurnMode } from 'src/app/enums/analystTurnMode.enum';
import { ChatMessageRole } from 'src/app/enums/chatMessageRole.enum';
import { AskErrorKind } from 'src/app/enums/askErrorKind.enum';
import { AnalystProgressPhase } from 'src/app/enums/analystProgressPhase.enum';
import { SecurityProfileEnum } from 'src/app/enums/securityProfile.enum';
import { findFirstReadableAiAnalystAdminTab, getAiAnalystAdminRouteConfig } from 'src/app/shared/constants/aiAnalystAdminRouteConfig.constants';
import { isGuid } from 'src/app/_helpers/guid.helper';
import { AuthService } from 'src/app/services';
import { PermissionService } from 'src/app/services/permission.service';
import { ChatService } from 'src/app/services/chat.service';
import { CitationPanelService } from 'src/app/services/citation-panel.service';
import { ToastService } from 'src/app/services/toast.service';
import { UserService } from 'src/app/services/user.service';
import { TranslateService } from 'src/app/services/translate.service';
import { DeleteDialog } from 'src/app/shared/delete-dialog/delete-dialog.component';
import { CHAT_TITLE_MAX_LENGTH, MAX_QUESTION_CHARS } from 'src/app/shared/constants/chat.constants';
import { AI_ANALYST_ROUTE } from 'src/app/shared/utils/ai-analyst-route.util';
import { classifyAskError, extractAskErrorMessage, extractRetryAfterSeconds, isUsableMessage } from 'src/app/shared/utils/ask-error.util';
import { buildChatMarkdown } from 'src/app/shared/utils/chat-markdown-export.util';
import { getCitationPanelWidth } from 'src/app/shared/utils/citation-preview.util';
import { normalizeChatMessageContent } from 'src/app/shared/utils/chat-message-validation.util';
import { downloadBlob } from 'src/app/shared/utils/download.util';
import { ChatComposerComponent } from 'src/app/components/ai-analyst/chat-composer/chat-composer.component';
import { CitationPanelComponent } from 'src/app/components/ai-analyst/citation-panel/citation-panel.component';
import { FlyoutContainerComponent } from 'src/app/shared/flyout/flyout-container/flyout-container.component';

const TITLE_MAX_LENGTH = 48;
const PROGRESS_POLL_INTERVAL_MS = 1500;
const REPORT_POLL_INTERVAL_MS = 5000;
const REPORT_POLL_MAX_CONSECUTIVE_FAILURES = 3;

@Component({
  standalone: false,
  selector: 'app-ai-analyst',
  templateUrl: './ai-analyst.component.html',
  styleUrls: ['./ai-analyst.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AiAnalystComponent implements OnInit, AfterViewInit, OnDestroy {
  chats: ChatResponse[] = [];
  activeChat: ChatResponse | null = null;
  messages: ChatMessageResponse[] = [];
  isAsking = false;
  askStopping = false;
  askCancelled = false;
  askError: AskErrorKind | null = null;
  askErrorMessage: string | null = null;
  askRetryAfterSeconds: number | null = null;
  analystDisabled = false;
  currentProgressPhase: AnalystProgressPhase | null = null;
  currentProgressSteps: AnalystProgressStep[] = [];
  /** Completed turns' step history, keyed by the resulting assistant message id, for the collapsed
   * "Worked for Xs · N steps" summary the finished thread keeps under that message. Session-only -
   * never persisted, never populated for messages loaded from chat history. */
  turnStepSummaries: Record<string, AnalystTurnStepSummary> = {};
  isGeneratingReport = false;
  reportJobStatus: AiReportJobStatus | null = null;
  reportJobErrorMessage: string | null = null;
  reportCancelRequested = false;
  reportConnectionLost = false;
  railOpen = false;
  isEditingTitle = false;
  titleInput = '';

  readonly renameMaxLength = CHAT_TITLE_MAX_LENGTH;

  @ViewChild('composer') private composer?: ChatComposerComponent;
  @ViewChild(FlyoutContainerComponent) private citationFlyout?: FlyoutContainerComponent;

  private readonly destroy$ = new Subject<void>();
  private readonly cancelAsk$ = new Subject<void>();
  private readonly cancelReportPoll$ = new Subject<void>();
  private paintFlushTimer = 0;
  /** Bumped at the start of every ask/rewind turn. A turn's completion handlers only touch shared
   * in-flight state (isAsking, askStopping, currentProgressPhase, navigation) when their captured token
   * still matches - otherwise a newer turn is in charge and the stale one just contributes its message. */
  private askToken = 0;
  private lastAskContent = '';
  private lastRewindToMessageId: string | null = null;
  private activeReportJobId: string | null = null;
  private activeAskJobId: string | null = null;

  readonly creditsAndSettingsRoute: string | null;

  constructor(
    private readonly chatService: ChatService,
    private readonly toastService: ToastService,
    private readonly translateService: TranslateService,
    private readonly dialog: MatDialog,
    private readonly cdr: ChangeDetectorRef,
    private readonly zone: NgZone,
    private readonly route: ActivatedRoute,
    private readonly router: Router,
    private readonly citationPanelService: CitationPanelService,
    private readonly authService: AuthService,
    private readonly permissionService: PermissionService,
    private readonly userService: UserService,
  ) {
    this.creditsAndSettingsRoute = this.resolveCreditsAndSettingsRoute();
  }

  ngOnInit(): void {
    this.chatService
      .getChats()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (chats) => {
          this.chats = chats;
          this.cdr.markForCheck();
        },
        error: (err) => {
          this.handleLoadError(err);
          this.cdr.markForCheck();
        },
      });

    this.route.paramMap
      .pipe(
        takeUntil(this.destroy$),
        map((params) => params.get('chatId')),
        filter((chatId): chatId is string => {
          if (!chatId || chatId === this.activeChat?.id) {
            return false;
          }
          if (!isGuid(chatId)) {
            this.navigateToChat(null, false);
            return false;
          }
          return true;
        }),
        switchMap((chatId) =>
          this.loadChat(chatId).pipe(
            catchError((err) => {
              this.handleLoadError(err);
              this.navigateToChat(null, false);
              this.cdr.markForCheck();
              return EMPTY;
            }),
          ),
        ),
      )
      .subscribe();

    this.citationPanelService.citationRequested$
      .pipe(takeUntil(this.destroy$))
      .subscribe({ next: (citation) => this.openCitationPanel(citation) });
  }

  ngAfterViewInit(): void {
    // Forces a repaint so Chromium redraws the composited sidenav icons after this view loads.
    this.zone.runOutsideAngular(() => {
      const flush = () => window.dispatchEvent(new Event('resize'));
      requestAnimationFrame(() => requestAnimationFrame(flush));
      this.paintFlushTimer = window.setTimeout(flush, 500);
    });
  }

  ngOnDestroy(): void {
    clearTimeout(this.paintFlushTimer);
    this.destroy$.next();
    this.destroy$.complete();
  }

  onNewChat(): void {
    this.cancelAsk$.next();
    this.isAsking = false;
    this.askStopping = false;
    this.askCancelled = false;
    this.activeAskJobId = null;
    this.stopReportGeneration();
    this.activeChat = null;
    this.messages = [];
    this.clearAskState();
    this.closeRail();
    this.closeCitationPanel();
    this.navigateToChat(null, false);
    this.cdr.markForCheck();
  }

  onSend(content: string): void {
    const trimmed = content.trim();
    if (!trimmed || this.isAsking || this.analystDisabled) {
      return;
    }

    const optimisticUserMessage: ChatMessageResponse = {
      id: '',
      chatId: this.activeChat?.id ?? '',
      role: ChatMessageRole.User,
      content: trimmed,
      createdAt: new Date().toISOString(),
      citations: [],
      outputs: [],
      rating: null,
      stopReason: null,
      hasIncompleteData: false,
    };
    this.appendMessage(optimisticUserMessage);
    this.askError = null;
    this.askErrorMessage = null;
    this.askRetryAfterSeconds = null;
    this.lastAskContent = trimmed;
    this.lastRewindToMessageId = null;
    this.runAsk(trimmed);
  }

  retryLastAsk(): void {
    if (!this.lastAskContent || this.isAsking || this.analystDisabled) {
      return;
    }
    this.askError = null;
    this.askErrorMessage = null;
    this.askRetryAfterSeconds = null;
    if (this.lastRewindToMessageId) {
      this.runRewindAsk(this.lastAskContent, this.lastRewindToMessageId);
      return;
    }
    this.runAsk(this.lastAskContent);
  }

  /** Re-runs the user turn that produced `assistantMessage`, resolved from the message immediately
   * preceding it. Only offered by the thread on the last assistant message when nothing is in flight. */
  onRegenerate(assistantMessage: ChatMessageResponse): void {
    if (this.isAsking || this.isGeneratingReport || this.analystDisabled || !this.activeChat) {
      return;
    }
    const precedingUserMessage = this.findPrecedingUserMessage(assistantMessage.id);
    if (!precedingUserMessage) {
      return;
    }
    this.runRewindAsk(precedingUserMessage.content, precedingUserMessage.id);
  }

  /** Resends an edited user message. Only offered by the thread on the last user message when nothing is
   * in flight; the content has already passed the composer's own validation there. */
  onEditAndResend(event: { messageId: string; content: string }): void {
    if (this.isAsking || this.isGeneratingReport || this.analystDisabled || !this.activeChat) {
      return;
    }
    const trimmed = normalizeChatMessageContent(event.content, MAX_QUESTION_CHARS);
    if (trimmed === null) {
      return;
    }
    this.runRewindAsk(trimmed, event.messageId);
  }

  /** Downloads the currently active thread as a Markdown file, rendered entirely client-side. */
  onExportConversation(): void {
    if (!this.activeChat) {
      return;
    }
    const markdown = buildChatMarkdown(this.activeChat.title, this.messages);
    downloadBlob(new Blob([markdown], { type: 'text/markdown' }), `${this.activeChat.title}.md`);
  }

  onOpenCreditsAndSettings(): void {
    if (!this.creditsAndSettingsRoute) return;
    this.router.navigateByUrl(this.creditsAndSettingsRoute);
  }

  onGenerateReport(content: string): void {
    const trimmed = content.trim();
    if (!trimmed || this.isAsking || this.isGeneratingReport || this.analystDisabled) {
      return;
    }

    const optimisticUserMessage: ChatMessageResponse = {
      id: '',
      chatId: this.activeChat?.id ?? '',
      role: ChatMessageRole.User,
      content: trimmed,
      createdAt: new Date().toISOString(),
      citations: [],
      outputs: [],
      rating: null,
      stopReason: null,
      hasIncompleteData: false,
    };
    this.appendMessage(optimisticUserMessage);
    this.askError = null;
    this.askErrorMessage = null;
    this.isGeneratingReport = true;
    this.reportJobStatus = AiReportJobStatus.Queued;
    this.reportJobErrorMessage = null;
    this.reportCancelRequested = false;
    this.reportConnectionLost = false;
    this.cdr.markForCheck();

    const stopReportPoll$ = new Subject<void>();
    const isNewChat = !this.activeChat;

    let submit$: Observable<ReportJobStatus>;
    if (this.activeChat) {
      submit$ = this.chatService.generateReport({ chatId: this.activeChat.id, content: trimmed });
    } else {
      submit$ = this.chatService.createChat({ title: this.buildTitle(trimmed) }).pipe(
        tap((chat) => {
          this.activeChat = chat;
          this.chats = [chat, ...this.chats];
          this.cdr.markForCheck();
        }),
        switchMap((chat) => this.chatService.generateReport({ chatId: chat.id, content: trimmed })),
      );
    }

    submit$.pipe(takeUntil(this.cancelReportPoll$), takeUntil(this.destroy$)).subscribe({
      next: (job) => {
        this.activeReportJobId = job.jobId;
        this.reportJobStatus = job.status;
        if (this.isTurnTerminal(job.status)) {
          this.finishReportJob(job, isNewChat);
        } else {
          this.startReportPolling(job.jobId, stopReportPoll$, isNewChat);
        }
        this.cdr.markForCheck();
      },
      error: (err) => {
        this.isGeneratingReport = false;
        this.reportJobStatus = AiReportJobStatus.Failed;
        this.reportJobErrorMessage = extractAskErrorMessage(err);
        this.reportConnectionLost = false;
        this.analystDisabled = classifyAskError(err) === AskErrorKind.AnalystDisabled;
        this.composer?.restoreContent(trimmed);
        this.navigateToChatOnceSettled(isNewChat);
        this.cdr.markForCheck();
      },
    });
  }

  onCancelReportGeneration(): void {
    if (!this.activeReportJobId || this.reportCancelRequested) {
      return;
    }
    this.reportCancelRequested = true;
    this.cdr.markForCheck();

    this.chatService
      .cancelReportJob(this.activeReportJobId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        error: (err) => {
          this.reportCancelRequested = false;
          this.toastService.showError(err);
          this.cdr.markForCheck();
        },
      });
  }

  /** Real cancel: reaches the model through the same CTS chain a report generation's cancel uses.
   * Stays in the stopping state until the terminal `Cancelled` status arrives from the poll, since the
   * server keeps working for up to `CancelPollIntervalSeconds` after the click. */
  onStopAsking(): void {
    if (!this.activeAskJobId || this.askStopping) {
      return;
    }
    this.askStopping = true;
    this.cdr.markForCheck();

    this.chatService
      .cancelTurn(this.activeAskJobId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        error: (err) => {
          this.askStopping = false;
          this.toastService.showError(err);
          this.cdr.markForCheck();
        },
      });
  }

  onChatSelected(chatId: string): void {
    if (chatId === this.activeChat?.id) {
      this.closeRail();
      return;
    }

    this.loadChat(chatId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.closeRail();
          this.navigateToChat(chatId, false);
        },
        error: (err) => {
          this.toastService.showError(err);
          this.cdr.markForCheck();
        },
      });
  }

  onStarToggled(event: { id: string; isStarred: boolean }): void {
    this.patchChatAndRefresh(event.id, { isStarred: event.isStarred });
  }

  onRenameCommitted(event: { id: string; title: string }): void {
    this.patchChatAndRefresh(event.id, { title: event.title });
  }

  startEditingTitle(): void {
    if (!this.activeChat) {
      return;
    }
    this.isEditingTitle = true;
    this.titleInput = this.activeChat.title;
    this.cdr.markForCheck();
  }

  commitTitleEdit(): void {
    if (!this.isEditingTitle || !this.activeChat) {
      return;
    }
    this.isEditingTitle = false;
    const trimmed = this.titleInput.trim();
    if (trimmed && trimmed !== this.activeChat.title) {
      this.patchChatAndRefresh(this.activeChat.id, { title: trimmed });
    }
    this.cdr.markForCheck();
  }

  cancelEditingTitle(): void {
    this.isEditingTitle = false;
    this.titleInput = '';
    this.cdr.markForCheck();
  }

  onDeleteRequested(chatId: string): void {
    const dialogRef = this.dialog.open(DeleteDialog, {
      data: {
        title: this.translateService.getDynamicTranslation('Chat'),
        service: null,
        customDelete: () => this.chatService.deleteChat(chatId),
      },
      width: '440px',
      disableClose: true,
    });

    dialogRef
      .afterClosed()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (confirmed: boolean) => {
          if (confirmed) {
            this.removeChatLocally(chatId);
          }
        },
      });
  }

  toggleRail(): void {
    this.railOpen = !this.railOpen;
  }

  closeRail(): void {
    this.railOpen = false;
  }

  private resolveCreditsAndSettingsRoute(): string | null {
    const currentUser = this.authService.getCurrentUser();
    const userType = currentUser?.userType;
    if (userType !== SecurityProfileEnum.Admin && userType !== SecurityProfileEnum.SuperAdmin) {
      return null;
    }

    const tabs = getAiAnalystAdminRouteConfig(userType === SecurityProfileEnum.SuperAdmin, !!currentUser.isAiAnalystEnabled);
    return findFirstReadableAiAnalystAdminTab(tabs, (objectId) => this.permissionService.hasReadPermission(objectId))?.routerLink ?? null;
  }

  /** Opens the citation preview panel beside the chat. Reused for every marker/source click across
   * prose, table, and chart citations (see CitationPanelService) - re-opening while already open
   * just swaps the panel's content, since FlyoutContainerComponent.open() clears its own container
   * first. */
  private openCitationPanel(citation: Citation): void {
    this.citationFlyout?.open(CitationPanelComponent, citation, getCitationPanelWidth(citation));
  }

  /** Called wherever the visible thread is swapped out wholesale (new chat, switching/loading a
   * chat, deleting the active one) - an open panel refers to a citation from the conversation that
   * just left the screen, so it shouldn't linger. Not called from onSend/regenerate/edit-and-resend:
   * those append to the same thread, so a citation already open is still valid. */
  private closeCitationPanel(): void {
    this.citationFlyout?.close();
  }

  private patchChatAndRefresh(id: string, request: PatchChatRequest): void {
    this.chatService
      .patchChat(id, request)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => this.refreshChats(),
        error: (err) => {
          this.toastService.showError(err);
          this.cdr.markForCheck();
        },
      });
  }

  private refreshChats(): void {
    this.chatService
      .getChats()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (chats) => {
          this.chats = chats;
          if (this.activeChat) {
            this.activeChat = chats.find((c) => c.id === this.activeChat?.id) ?? this.activeChat;
          }
          this.cdr.markForCheck();
        },
        error: (err) => {
          this.toastService.showError(err);
          this.cdr.markForCheck();
        },
      });
  }

  private removeChatLocally(id: string): void {
    this.chats = this.chats.filter((c) => c.id !== id);
    if (this.activeChat?.id === id) {
      this.cancelAsk$.next();
      this.isAsking = false;
      this.askStopping = false;
      this.askCancelled = false;
      this.activeAskJobId = null;
      this.stopReportGeneration();
      this.activeChat = null;
      this.messages = [];
      this.clearAskState();
      this.closeCitationPanel();
      this.navigateToChat(null, false);
    }
    this.cdr.markForCheck();
  }

  private navigateToChat(chatId: string | null, push: boolean): void {
    const commands = chatId ? [AI_ANALYST_ROUTE, chatId] : [AI_ANALYST_ROUTE];
    this.router.navigate(commands, { replaceUrl: !push });
  }

  /** Tears down any in-flight ask/report for the currently active chat, then loads `chatId` and resumes its turn job, of either mode, if one is running. Shared by chat-rail selection and URL-driven navigation so both paths behave identically. */
  private loadChat(chatId: string): Observable<void> {
    this.cancelAsk$.next();
    this.isAsking = false;
    this.askStopping = false;
    this.askCancelled = false;
    this.activeAskJobId = null;
    this.stopReportGeneration();
    this.clearAskState();
    this.closeCitationPanel();

    return this.chatService.getChatWithMessages(chatId).pipe(
      map((result) => {
        const { messages, activeReportJob, ...chat } = result;
        this.activeChat = chat;
        this.messages = messages;
        this.chatService.refreshCreditBalance();
        this.resumeActiveTurnJob(activeReportJob);
        this.cdr.markForCheck();
      }),
    );
  }

  private runAsk(trimmed: string): void {
    const token = ++this.askToken;
    this.isAsking = true;
    this.askStopping = false;
    this.askCancelled = false;
    this.activeAskJobId = null;
    this.currentProgressPhase = null;
    this.currentProgressSteps = [];
    this.cdr.markForCheck();

    const stopPolling$ = new Subject<void>();
    const isNewChat = !this.activeChat;

    let submit$: Observable<ReportJobStatus>;
    if (this.activeChat) {
      submit$ = this.chatService.submitAsk({ chatId: this.activeChat.id, content: trimmed });
    } else {
      submit$ = this.chatService.createChat({ title: this.buildTitle(trimmed) }).pipe(
        tap((chat) => {
          this.activeChat = chat;
          this.chats = [chat, ...this.chats];
          this.cdr.markForCheck();
        }),
        switchMap((chat) => this.chatService.submitAsk({ chatId: chat.id, content: trimmed })),
      );
    }

    submit$.pipe(takeUntil(this.cancelAsk$), takeUntil(this.destroy$)).subscribe({
      next: (job) => {
        if (token !== this.askToken) {
          return;
        }
        this.activeAskJobId = job.jobId;
        if (this.isTurnTerminal(job.status)) {
          this.finishAskTurn({ ...job, phase: null, hasIncompleteData: false }, isNewChat, false);
        } else {
          this.startTurnPolling(job.jobId, stopPolling$, (status, connectionLost) => {
            if (token === this.askToken) {
              this.finishAskTurn(status, isNewChat, connectionLost);
            }
          });
        }
        this.cdr.markForCheck();
      },
      error: (err) => {
        this.chatService.refreshCreditBalance();
        if (token === this.askToken) {
          this.isAsking = false;
          this.askStopping = false;
          this.currentProgressPhase = null;
          this.currentProgressSteps = [];
          this.askError = classifyAskError(err);
          this.askErrorMessage = extractAskErrorMessage(err);
          this.askRetryAfterSeconds = extractRetryAfterSeconds(err);
          this.analystDisabled = this.askError === AskErrorKind.AnalystDisabled;
          this.composer?.restoreContent(this.lastAskContent);
          this.navigateToChatOnceSettled(isNewChat);
        }
        this.cdr.markForCheck();
      },
    });
  }

  private navigateToChatOnceSettled(isNewChat: boolean): void {
    if (isNewChat && this.activeChat) {
      this.navigateToChat(this.activeChat.id, true);
    }
  }

  private findPrecedingUserMessage(assistantMessageId: string): ChatMessageResponse | null {
    const index = this.messages.findIndex((m) => m.id === assistantMessageId);
    for (let i = index - 1; i >= 0; i--) {
      if (this.messages[i].role === ChatMessageRole.User) {
        return this.messages[i];
      }
    }
    return null;
  }

  /** Runs a rewind turn: the server supersedes `rewindToMessageId` and everything after it, then answers
   * `content` as a normal turn. The thread can't be patched locally since messages disappeared server-side,
   * so a successful turn re-fetches the whole chat through `loadChat` instead of appending the response.
   * The supersede commits before the new turn can fail, so a failed turn resyncs too (`resyncMessages`) -
   * otherwise the thread keeps showing an answer the server already retracted, or misses a real one the
   * server persisted after the client gave up on the request. */
  private runRewindAsk(content: string, rewindToMessageId: string): void {
    const chatId = this.activeChat!.id;
    const token = ++this.askToken;
    this.isAsking = true;
    this.askStopping = false;
    this.askCancelled = false;
    this.activeAskJobId = null;
    this.currentProgressPhase = null;
    this.currentProgressSteps = [];
    this.askError = null;
    this.askErrorMessage = null;
    this.askRetryAfterSeconds = null;
    this.lastAskContent = content;
    this.lastRewindToMessageId = rewindToMessageId;
    this.cdr.markForCheck();

    const stopPolling$ = new Subject<void>();

    this.chatService
      .submitAsk({ chatId, content, rewindToMessageId })
      .pipe(takeUntil(this.cancelAsk$), takeUntil(this.destroy$))
      .subscribe({
        next: (job) => {
          if (token !== this.askToken) {
            return;
          }
          this.activeAskJobId = job.jobId;
          if (this.isTurnTerminal(job.status)) {
            this.finishRewindAskTurn(chatId, token, { ...job, phase: null, hasIncompleteData: false }, false);
          } else {
            this.startTurnPolling(job.jobId, stopPolling$, (status, connectionLost) =>
              this.finishRewindAskTurn(chatId, token, status, connectionLost),
            );
          }
          this.cdr.markForCheck();
        },
        error: (err) => {
          this.chatService.refreshCreditBalance();
          if (token === this.askToken) {
            this.isAsking = false;
            this.askStopping = false;
            this.currentProgressPhase = null;
            this.currentProgressSteps = [];
            this.askError = classifyAskError(err);
            this.askErrorMessage = extractAskErrorMessage(err);
            this.askRetryAfterSeconds = extractRetryAfterSeconds(err);
            this.analystDisabled = this.askError === AskErrorKind.AnalystDisabled;
            this.resyncMessages(chatId)
              .pipe(takeUntil(this.destroy$))
              .subscribe({ error: (refetchErr) => this.handleLoadError(refetchErr) });
          }
          this.cdr.markForCheck();
        },
      });
  }

  /** Terminal handling for a rewound turn. Rewound history is authoritative from the server regardless
   * of whether a newer turn has since started, and the supersede commits before this turn can fail or
   * be cancelled, so every terminal status resyncs the thread - a `Completed` status through the full
   * `loadChat` (no token gate needed, see below), `Cancelled`/`Failed` through `resyncMessages` so the
   * askError banner it also sets isn't wiped by `loadChat`'s own state reset. */
  private finishRewindAskTurn(chatId: string, token: number, status: AnalystTurnStatus, connectionLost: boolean): void {
    if (token === this.askToken) {
      this.activeAskJobId = null;
      this.askStopping = false;
    }

    if (status.status === AiReportJobStatus.Completed) {
      this.loadChat(chatId)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          error: (err) => {
            this.handleLoadError(err);
            this.cdr.markForCheck();
          },
        });
      return;
    }

    this.chatService.refreshCreditBalance();
    if (token !== this.askToken) {
      return;
    }
    this.isAsking = false;
    this.currentProgressPhase = null;
    this.currentProgressSteps = [];
    if (status.status === AiReportJobStatus.Cancelled) {
      this.askCancelled = true;
    } else {
      this.askError = connectionLost ? AskErrorKind.ConnectionLost : AskErrorKind.ServerFailure;
      this.askErrorMessage = status.errorMessage && isUsableMessage(status.errorMessage) ? status.errorMessage : null;
      this.askRetryAfterSeconds = null;
    }
    this.resyncMessages(chatId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({ error: (refetchErr) => this.handleLoadError(refetchErr) });
    this.cdr.markForCheck();
  }

  /** Chat reads are rejected with a 403 once the tenant is switched off, so a stale session that still
   * thinks the analyst is on lands here: show the disabled state and sync the session flag instead of a
   * generic error toast. */
  private handleLoadError(err: unknown): void {
    if (classifyAskError(err) !== AskErrorKind.AnalystDisabled) {
      this.toastService.showError(err);
      return;
    }
    this.userService
      .getCurrentUserProfile()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (profile) => {
          if (profile.isAiAnalystEnabled !== false) {
            this.toastService.showError(err);
            return;
          }
          this.askError = AskErrorKind.AnalystDisabled;
          this.askErrorMessage = extractAskErrorMessage(err);
          this.askRetryAfterSeconds = null;
          this.analystDisabled = true;
          this.authService.setCurrentUserAiAnalystEnabled(false);
          this.cdr.markForCheck();
        },
      });
  }

  /** Re-fetches `chatId`'s messages and applies them without touching ask/report-in-flight state,
   * for callers that need the thread resynced without the wider teardown `loadChat` performs.
   * Discards its own response if the user has since switched chats, since the subscription
   * outlives the active chat and would otherwise overwrite the newly loaded one. */
  private resyncMessages(chatId: string): Observable<void> {
    return this.chatService.getChatWithMessages(chatId).pipe(
      map((result) => {
        if (this.activeChat?.id !== chatId) {
          return;
        }
        const { messages, ...chat } = result;
        this.activeChat = chat;
        this.messages = messages;
        this.cdr.markForCheck();
      }),
    );
  }

  /** Polls a submitted ask turn's lifecycle status and coarse progress phase, on the same interval and
   * failure tolerance the report poller already uses. `onTerminal` lets `runAsk` and `runRewindAsk` share
   * this one poller while resolving a finished turn differently - an ordinary ask patches the message it
   * appends, a rewind resyncs the whole chat. Stopped via `stopPoll$` (this turn ending), `cancelAsk$` (a
   * newer ask superseding it), or `destroy$`. */
  private startTurnPolling(jobId: string, stopPoll$: Subject<void>, onTerminal: (status: AnalystTurnStatus, connectionLost: boolean) => void): void {
    let consecutiveFailures = 0;
    const startedAt = Date.now();
    let stepsSoFar: AnalystProgressStep[] = [];

    timer(0, PROGRESS_POLL_INTERVAL_MS)
      .pipe(
        switchMap(() => this.chatService.getTurnStatus(jobId).pipe(catchError(() => of(null)))),
        takeUntil(stopPoll$),
        takeUntil(this.cancelAsk$),
        takeUntil(this.destroy$),
      )
      .subscribe((status) => {
        if (status === null) {
          consecutiveFailures += 1;
          if (consecutiveFailures < REPORT_POLL_MAX_CONSECUTIVE_FAILURES) {
            return;
          }
          stopPoll$.next();
          onTerminal(this.buildFailedTurnStatus(jobId), true);
          return;
        }

        consecutiveFailures = 0;
        this.currentProgressPhase = status.phase;
        stepsSoFar = this.mergeReportedSteps(stepsSoFar, status.steps);
        this.currentProgressSteps = stepsSoFar;
        if (this.isTurnTerminal(status.status)) {
          stopPoll$.next();
          this.recordTurnStepSummary(status, stepsSoFar, startedAt);
          onTerminal(status, false);
          return;
        }
        this.cdr.markForCheck();
      });
  }

  /** The server drops its progress entry the moment the tool loop ends, strictly before the job flips
   * to a terminal status, so the poll that finally sees the answer can report no steps at all. Steps
   * only ever grow within a turn, so a shorter later report is always that gap, never a real regression
   * - but "longer" alone isn't proof it's the same turn. `SubmitAsk`'s own guard is advisory rather than
   * a hard invariant (read-then-write, no unique index, existing chats may already hold two non-terminal
   * jobs), so two orchestrators can genuinely share one progress key. Before steps existed the worst case
   * was a flickering label; with a list, adopting whichever report is merely longer can latch a foreign
   * turn's steps and keep them - including into the saved summary under the wrong answer. Requiring the
   * new report to be a prefix-extension of what's already known (every already-seen step still present,
   * in place, phase and duration unchanged) rejects a foreign list outright: it diverges from position 0
   * because it's tracking a different turn's tool calls, so the check fails before length is ever the
   * deciding factor. */
  private mergeReportedSteps(known: AnalystProgressStep[], reported: AnalystProgressStep[] | undefined): AnalystProgressStep[] {
    const steps = reported ?? [];
    if (steps.length < known.length) {
      return known;
    }
    const isPrefixExtension = known.every((step, index) => this.stepsMatch(step, steps[index]));
    return isPrefixExtension ? steps : known;
  }

  private stepsMatch(a: AnalystProgressStep, b: AnalystProgressStep): boolean {
    return a.phase === b.phase && a.durationMs === b.durationMs;
  }

  /** Attaches a "Worked for Xs · N steps" summary to the turn's resulting message once it lands,
   * so the finished thread can render it collapsed under that message. `totalMs` is measured from
   * `startedAt` (when this poller started) since the server never reports total elapsed time. Skipped
   * for a thinking-only turn (no completed steps) or a turn with no resulting message (cancelled,
   * failed) - there's nothing real to summarize. */
  private recordTurnStepSummary(status: AnalystTurnStatus, steps: AnalystProgressStep[], startedAt: number): void {
    if (status.resultChatMessageId === null || steps.length === 0) {
      return;
    }
    this.turnStepSummaries = {
      ...this.turnStepSummaries,
      [status.resultChatMessageId]: { steps, totalMs: Date.now() - startedAt },
    };
  }

  /** A single dropped poll must never mark a healthy turn failed - a consecutive-failure count in this
   * shape is fabricated only once the tolerance above is actually exhausted, and callers must treat it as
   * `connectionLost`, never as a genuine server-sent `Failed`. */
  private buildFailedTurnStatus(jobId: string): AnalystTurnStatus {
    return {
      jobId,
      chatId: this.activeChat?.id ?? '',
      mode: AnalystTurnMode.Answer,
      status: AiReportJobStatus.Failed,
      phase: null,
      resultChatMessageId: null,
      stopReason: null,
      hasIncompleteData: false,
      errorMessage: null,
    };
  }

  private startReportPolling(jobId: string, stopPoll$: Subject<void>, isNewChat: boolean): void {
    let consecutiveFailures = 0;
    const startedAt = Date.now();
    let stepsSoFar: AnalystProgressStep[] = [];

    timer(REPORT_POLL_INTERVAL_MS, REPORT_POLL_INTERVAL_MS)
      .pipe(
        switchMap(() => this.chatService.getTurnStatus(jobId).pipe(catchError(() => of(null)))),
        takeUntil(stopPoll$),
        takeUntil(this.cancelReportPoll$),
        takeUntil(this.destroy$),
      )
      .subscribe((job) => {
        if (job === null) {
          consecutiveFailures += 1;
          if (consecutiveFailures < REPORT_POLL_MAX_CONSECUTIVE_FAILURES) {
            return;
          }
          stopPoll$.next();
          this.isGeneratingReport = false;
          this.activeReportJobId = null;
          this.reportJobStatus = AiReportJobStatus.Failed;
          this.reportJobErrorMessage = null;
          this.reportCancelRequested = false;
          this.reportConnectionLost = true;
          this.currentProgressPhase = null;
          this.currentProgressSteps = [];
          this.navigateToChatOnceSettled(isNewChat);
          this.cdr.markForCheck();
          return;
        }

        consecutiveFailures = 0;
        this.reportJobStatus = job.status;
        this.currentProgressPhase = job.phase;
        stepsSoFar = this.mergeReportedSteps(stepsSoFar, job.steps);
        this.currentProgressSteps = stepsSoFar;
        if (this.isTurnTerminal(job.status)) {
          stopPoll$.next();
          this.recordTurnStepSummary(job, stepsSoFar, startedAt);
          this.finishReportJob(job, isNewChat);
          return;
        }
        this.cdr.markForCheck();
      });
  }

  private isTurnTerminal(status: AiReportJobStatus): boolean {
    return status !== AiReportJobStatus.Queued && status !== AiReportJobStatus.Running;
  }

  private finishReportJob(job: ReportJobStatus, isNewChat: boolean): void {
    this.isGeneratingReport = false;
    this.activeReportJobId = null;
    this.reportJobStatus = job.status;
    this.reportJobErrorMessage = job.errorMessage && isUsableMessage(job.errorMessage) ? job.errorMessage : null;
    this.reportCancelRequested = false;
    this.reportConnectionLost = false;
    this.currentProgressPhase = null;
    this.currentProgressSteps = [];

    if (job.resultChatMessageId) {
      this.refreshMessagesForReport(job.chatId, isNewChat);
      return;
    }

    this.chatService.refreshCreditBalance();
    this.navigateToChatOnceSettled(isNewChat);
    this.cdr.markForCheck();
  }

  /** Terminal handling for an ordinary (non-rewind) ask turn, reached either immediately off the submit
   * response or off a terminal turn-status poll. */
  private finishAskTurn(status: AnalystTurnStatus, isNewChat: boolean, connectionLost: boolean): void {
    this.activeAskJobId = null;
    this.askStopping = false;

    if (status.status === AiReportJobStatus.Completed) {
      this.completeAskTurn(status, isNewChat);
      return;
    }

    this.isAsking = false;
    this.currentProgressPhase = null;
    this.currentProgressSteps = [];
    this.chatService.refreshCreditBalance();

    if (status.status === AiReportJobStatus.Cancelled) {
      // No assistant message by design (see AiReportJob's doc comment) - the thread keeps the user's
      // message with nothing under it, and the stopped marker makes that a deliberate state rather
      // than a lost turn.
      this.askCancelled = true;
    } else {
      this.askError = connectionLost ? AskErrorKind.ConnectionLost : AskErrorKind.ServerFailure;
      this.askErrorMessage = status.errorMessage && isUsableMessage(status.errorMessage) ? status.errorMessage : null;
      this.askRetryAfterSeconds = null;
    }
    this.navigateToChatOnceSettled(isNewChat);
    this.cdr.markForCheck();
  }

  /** A completed turn's answer only exists once the poll reports it, so it's fetched through
   * `resyncMessages` rather than appended from the (now nonexistent) synchronous response, then the
   * refetched message - which `mapMessage` always stamps with `stopReason: null, hasIncompleteData: false`
   * since neither is part of the wire message - is patched with the real values from this status. */
  private completeAskTurn(status: AnalystTurnStatus, isNewChat: boolean): void {
    this.resyncMessages(status.chatId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          if (status.resultChatMessageId) {
            this.applyTurnResultToMessage(status.resultChatMessageId, status.stopReason, status.hasIncompleteData);
          }
          this.chatService.refreshCreditBalance();
          this.isAsking = false;
          this.currentProgressPhase = null;
          this.currentProgressSteps = [];
          this.navigateToChatOnceSettled(isNewChat);
          this.cdr.markForCheck();
        },
        error: (err) => {
          // The turn completed and drew down credits regardless of this refetch failing, so the
          // balance is refreshed on both branches or it stays stale until some later action.
          this.chatService.refreshCreditBalance();
          this.handleLoadError(err);
          this.isAsking = false;
          this.currentProgressPhase = null;
          this.currentProgressSteps = [];
          this.navigateToChatOnceSettled(isNewChat);
          this.cdr.markForCheck();
        },
      });
  }

  private applyTurnResultToMessage(messageId: string, stopReason: AnalystStopReason | null, hasIncompleteData: boolean): void {
    this.messages = this.messages.map((message) => (message.id === messageId ? { ...message, stopReason, hasIncompleteData } : message));
  }

  private resumeActiveTurnJob(job: ReportJobStatus | null | undefined): void {
    if (!job) {
      return;
    }

    if (job.mode === AnalystTurnMode.Report) {
      this.isGeneratingReport = true;
      this.reportJobStatus = job.status;
      this.reportJobErrorMessage = null;
      this.reportCancelRequested = false;
      this.reportConnectionLost = false;
      this.activeReportJobId = job.jobId;

      const stopReportPoll$ = new Subject<void>();
      this.startReportPolling(job.jobId, stopReportPoll$, false);
      return;
    }

    const token = ++this.askToken;
    this.isAsking = true;
    this.askStopping = false;
    this.askCancelled = false;
    this.currentProgressPhase = null;
    this.currentProgressSteps = [];
    this.activeAskJobId = job.jobId;

    const stopPolling$ = new Subject<void>();
    this.startTurnPolling(job.jobId, stopPolling$, (status, connectionLost) => {
      if (token === this.askToken) {
        this.finishAskTurn(status, false, connectionLost);
      }
    });
  }

  private refreshMessagesForReport(chatId: string, isNewChat: boolean): void {
    this.resyncMessages(chatId)
      .pipe(takeUntil(this.cancelReportPoll$), takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.reportJobStatus = null;
          this.reportJobErrorMessage = null;
          this.chatService.refreshCreditBalance();
          this.navigateToChatOnceSettled(isNewChat);
          this.cdr.markForCheck();
        },
        error: (err) => {
          this.handleLoadError(err);
          this.navigateToChatOnceSettled(isNewChat);
          this.cdr.markForCheck();
        },
      });
  }

  private stopReportGeneration(): void {
    this.cancelReportPoll$.next();
    this.isGeneratingReport = false;
    this.reportJobStatus = null;
    this.reportJobErrorMessage = null;
    this.reportCancelRequested = false;
    this.reportConnectionLost = false;
    this.activeReportJobId = null;
    this.currentProgressPhase = null;
    this.currentProgressSteps = [];
  }

  private clearAskState(): void {
    if (this.analystDisabled) {
      return;
    }
    this.askError = null;
    this.askErrorMessage = null;
    this.askRetryAfterSeconds = null;
    this.lastAskContent = '';
    this.lastRewindToMessageId = null;
    this.currentProgressPhase = null;
    this.currentProgressSteps = [];
  }

  private buildTitle(text: string): string {
    return text.length > TITLE_MAX_LENGTH ? `${text.slice(0, TITLE_MAX_LENGTH)}…` : text;
  }

  /** Appends and re-sorts by `createdAt` rather than blindly pushing, since the optimistic user message
   * this is called with is stamped client-side before the server assigns its real `createdAt`, and a
   * resync landing around the same moment must not jumble the thread out of chronological order. */
  private appendMessage(message: ChatMessageResponse): void {
    this.messages = [...this.messages, message].sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime());
  }
}
